import React, { useState } from 'react';
import { Download, FileText, CheckCircle, Phone, AlertTriangle } from 'lucide-react';

const DownloadableChecklists = ({ language = 'en' }) => {
  const [downloading, setDownloading] = useState(null);

  const checklists = {
    en: [
      {
        id: 'arrest-response',
        title: 'Immediate Response to Arrest Checklist',
        description: 'What to do in the first 30 minutes after learning of an arrest',
        items: [
          'Write down arrest time and location',
          'Get the defendant\'s full legal name',
          'Find out which jail they were taken to', 
          'Gather your identification documents',
          'Note any special circumstances (medical needs, etc.)',
          'Call Sailors Bail Bonds: (916) 833-0052',
          'Do NOT attempt to contact the defendant directly',
          'Prepare payment method information'
        ],
        icon: '🚨',
        urgency: 'IMMEDIATE'
      },
      {
        id: 'bail-paperwork',
        title: 'Bail Paperwork Preparation Checklist',
        description: 'Documents and information you\'ll need for the bail process',
        items: [
          'Government-issued photo ID (co-signer)',
          'Proof of employment and income',
          'Bank statements (last 2 months)',
          'Property ownership documents (if using as collateral)',
          'Vehicle title (if using as collateral)',
          'Emergency contact information',
          'Defendant\'s personal information sheet',
          'Payment method ready (card/banking info)'
        ],
        icon: '📋',
        urgency: 'BEFORE SIGNING'
      },
      {
        id: 'court-compliance',
        title: 'Court Date Compliance Checklist',
        description: 'Ensuring the defendant meets all bail conditions',
        items: [
          'Write down all court dates immediately',
          'Set multiple reminders (phone, calendar, family)',
          'Confirm court location and parking',
          'Arrange transportation to court',
          'Check for any travel restrictions',
          'Understand curfew requirements',
          'Know contact reporting requirements',
          'Save our 24/7 number for questions'
        ],
        icon: '⚖️',
        urgency: 'ONGOING'
      },
      {
        id: 'family-support',
        title: 'Family Crisis Management Checklist',
        description: 'Supporting your family through this difficult time',
        items: [
          'Designate one person as primary communicator',
          'Inform close family members appropriately',
          'Arrange childcare if needed',
          'Take time off work if necessary',
          'Keep detailed records of all calls and costs',
          'Prepare for emotional support needs',
          'Consider counseling resources',
          'Plan for media attention if applicable'
        ],
        icon: '👨‍👩‍👧‍👦',
        urgency: 'AS NEEDED'
      }
    ],
    es: [
      {
        id: 'respuesta-arresto',
        title: 'Lista de Respuesta Inmediata al Arresto',
        description: 'Qué hacer en los primeros 30 minutos después de enterarse de un arresto',
        items: [
          'Anote la hora y lugar del arresto',
          'Obtenga el nombre legal completo del detenido',
          'Averigüe a qué cárcel fue llevado',
          'Reúna sus documentos de identificación',
          'Anote cualquier circunstancia especial (necesidades médicas, etc.)',
          'Llame a Sailors Bail Bonds: (916) 833-0052',
          'NO trate de contactar al detenido directamente',
          'Prepare información del método de pago'
        ],
        icon: '🚨',
        urgency: 'INMEDIATO'
      }
    ]
  };

  const text = {
    en: {
      title: 'Downloadable Checklists',
      subtitle: 'Step-by-step guides you can print and follow during this stressful time',
      download: 'Download PDF',
      downloading: 'Generating PDF...',
      items: 'Items',
      urgency: 'When to Use',
      print: 'Print & Share',
      emergency: 'Emergency Contact',
      note: 'Keep these checklists handy - they\'ll guide you through each step when you\'re too stressed to think clearly.'
    },
    es: {
      title: 'Listas de Verificación Descargables',
      subtitle: 'Guías paso a paso que puede imprimir y seguir durante este tiempo estresante',
      download: 'Descargar PDF',
      downloading: 'Generando PDF...',
      items: 'Artículos',
      urgency: 'Cuándo Usar',
      print: 'Imprimir y Compartir',
      emergency: 'Contacto de Emergencia',
      note: 'Mantenga estas listas a mano - le guiarán a través de cada paso cuando esté demasiado estresado para pensar claramente.'
    }
  };

  const currentText = text?.[language] || text?.en;
  const currentChecklists = checklists?.[language] || checklists?.en;

  const handleDownload = async (checklistId) => {
    setDownloading(checklistId);
    
    // Simulate PDF generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In a real implementation, this would generate and download a PDF
    const checklist = currentChecklists?.find(c => c?.id === checklistId);
    const content = generatePDFContent(checklist);
    
    // Create downloadable link
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${checklist?.title?.replace(/\s+/g, '_')}.txt`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
    
    setDownloading(null);
  };

  const generatePDFContent = (checklist) => {
    return `
${checklist?.title?.toUpperCase()}
${checklist?.description}

URGENCY LEVEL: ${checklist?.urgency}

CHECKLIST ITEMS:
${checklist?.items?.map((item, index) => `${index + 1}. [ ] ${item}`)?.join('\n')}

EMERGENCY CONTACT:
Sailors Bail Bonds - Available 24/7
Phone: (916) 833-0052
Website: sailorsbailbonds.com

Generated on: ${new Date()?.toLocaleDateString()}

INSTRUCTIONS:
- Print this checklist and keep it with you
- Check off each item as you complete it
- Call us immediately if you have questions
- Keep our number handy at all times

Remember: You don't have to handle this alone. We're here to guide you through every step.
    `?.trim();
  };

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6 flex items-center justify-center">
          <FileText className="w-10 h-10 mr-4 text-blue-600" aria-hidden="true" />
          {currentText?.title}
        </h2>
        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
          {currentText?.subtitle}
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-8 mb-12">
        {currentChecklists?.map((checklist, index) => (
          <div key={checklist?.id} className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
              <div className="flex items-center mb-4">
                <span className="text-4xl mr-4" aria-hidden="true">{checklist?.icon}</span>
                <div>
                  <h3 className="text-xl font-bold leading-tight">{checklist?.title}</h3>
                  <p className="text-blue-100 text-sm mt-1">{checklist?.description}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" aria-hidden="true" />
                  <span className="font-semibold text-sm">{currentText?.urgency}: {checklist?.urgency}</span>
                </div>
                <div className="text-sm text-blue-100">
                  {checklist?.items?.length} {currentText?.items}
                </div>
              </div>
            </div>

            {/* Checklist Items Preview */}
            <div className="p-6">
              <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                {checklist?.items?.slice(0, 6)?.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
                {checklist?.items?.length > 6 && (
                  <div className="text-gray-500 text-sm text-center py-2 border-t border-gray-200">
                    {language === 'es' 
                      ? `+ ${checklist?.items?.length - 6} elementos más en PDF completo`
                      : `+ ${checklist?.items?.length - 6} more items in full PDF`
                    }
                  </div>
                )}
              </div>

              {/* Download Button */}
              <button
                onClick={() => handleDownload(checklist?.id)}
                disabled={downloading === checklist?.id}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-green-700 hover:to-green-800 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
              >
                {downloading === checklist?.id ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    {currentText?.downloading}
                  </>
                ) : (
                  <>
                    <Download className="w-6 h-6 mr-3" aria-hidden="true" />
                    {currentText?.download}
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Usage Instructions */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-8">
        <div className="flex items-start">
          <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mr-4 mt-1" aria-hidden="true" />
          <div>
            <h3 className="text-2xl font-bold text-amber-800 mb-4">
              {language === 'es' ? 'Cómo Usar Estas Listas' : 'How to Use These Checklists'}
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-amber-700">
              <div>
                <h4 className="font-bold mb-2">{currentText?.print}</h4>
                <ul className="space-y-1 text-sm">
                  <li>• {language === 'es' ? 'Imprima inmediatamente después de descargar' : 'Print immediately after downloading'}</li>
                  <li>• {language === 'es' ? 'Mantenga copias en casa y en el carro' : 'Keep copies at home and in car'}</li>
                  <li>• {language === 'es' ? 'Comparta con familiares clave' : 'Share with key family members'}</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-2">{currentText?.emergency}</h4>
                <ul className="space-y-1 text-sm">
                  <li>• {language === 'es' ? 'Guarde nuestro número en su teléfono' : 'Save our number in your phone'}</li>
                  <li>• {language === 'es' ? 'Llámenos si se siente abrumado' : 'Call us if you feel overwhelmed'}</li>
                  <li>• {language === 'es' ? 'Disponible 24/7 para orientación' : 'Available 24/7 for guidance'}</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-white rounded-xl border border-amber-200">
              <p className="text-amber-800 font-semibold text-lg text-center">
                {currentText?.note}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Contact Card */}
      <div className="mt-12 bg-gradient-to-r from-red-600 to-red-700 text-white p-8 rounded-2xl text-center">
        <h3 className="text-2xl font-bold mb-4">
          {language === 'es' ? 'Checklists Confusos? ¡Nosotros Lo Guiamos!' : 'Checklists Confusing? We\'ll Guide You!'}
        </h3>
        <p className="text-xl text-red-100 mb-6">
          {language === 'es' ?'No se preocupe por seguir cada paso - llámenos y lo guiaremos por teléfono.' :'Don\'t worry about following every step - call us and we\'ll walk you through it.'
          }
        </p>
        <a 
          href="tel:+19168330052"
          className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-red-50 transition-all inline-flex items-center"
        >
          <Phone className="w-6 h-6 mr-3" aria-hidden="true" />
          {language === 'es' ? 'Llamar Para Ayuda Ahora' : 'Call For Help Now'}: (916) 833-0052
        </a>
      </div>
    </div>
  );
};

export default DownloadableChecklists;